/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Theme, useTheme, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonBoxField } from 'components/form/field/SimNaoSwitchButtonBoxField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta02IgnoraEnum,
  ConsultaSeguimentoPergunta02Model,
  ConsultaSeguimentoPergunta02ResultadoExameEnum,
} from './model-consultaSeguimentoPergunta02'
import { hasPergunta02IgnoraAnySim, isPergunta02IgnoraAllNao } from './utils-consultaSeguimentoPergunta02'

interface ConsultaSeguimentoPergunta02FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta02Model>
}

export function ConsultaSeguimentoPergunta02Field(props: Readonly<ConsultaSeguimentoPergunta02FieldProps>) {
  const { name } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta02Model>(resolveName(name), {
    subscription: { value: true },
  })

  const hasAnySim = hasPergunta02IgnoraAnySim(value.PERGUNTA_02_IGNORA)
  const isAllNao = isPergunta02IgnoraAllNao(value.PERGUNTA_02_IGNORA)

  const isPergunta02IgnoraRespondida = hasAnySim || isAllNao

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta02Model>({
    name,
    calculateResultado,
    conditionToCalculate,
  })

  return (
    <VFlow>
      <ConsultaSeguimentoPerguntaHeader
        pergunta={MChatPerguntaEnum.PERGUNTA_02}
        title='“Você informou que você já se perguntou se o seu filho pode ser surdo. O que o levou a esse pensamento?”'
        status={value.status}
      />
      <VFlow vSpacing={0}>
        <ConsultaSeguimentoIndicatorStart />
        <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta02IgnoraEnum>
          name={name.PERGUNTA_02_IGNORA}
          label='Ele ou ela...'
          options={PERGUNTA_02_IGNORA_OPTIONS}
          required
        />
        {value.resultado && (
          <ConsultaSeguimentoIndicatorResultado
            text={{
              FALHOU: 'Sim para qualquer um dos dois',
              PASSOU: 'Não para ambos:',
            }}
            resultado={value.resultado}
          />
        )}
        {isPergunta02IgnoraRespondida && (
          <Fragment>
            <ConsultaSeguimentoIndicatorStep text='Pergunte para todas as crianças, independente da pontuação:' />
            <VFlow>
              <SimNaoSwitchButtonBoxField
                name={name.PERGUNTA_02_FEZ_EXAME}
                label='“O seu filho fez exame auditivo?”'
                headingLevel={3}
                style={styles.fezExameBox}
                required
              />
              <RadioGroupBoxField<ConsultaSeguimentoPergunta02ResultadoExameEnum>
                name={name.PERGUNTA_02_RESULTADO_EXAME}
                label='Quais os resultados do teste auditivo?'
                tooltipText={
                  value?.PERGUNTA_02_FEZ_EXAME !== SimNaoEnum.SIM && 'Apenas necessário se responder "Sim" na anterior'
                }
                disabled={value?.PERGUNTA_02_FEZ_EXAME !== SimNaoEnum.SIM}
                options={PERGUNTA_02_RESULTADO_EXAME_OPTIONS}
              />
            </VFlow>
          </Fragment>
        )}
      </VFlow>
    </VFlow>
  )
}

const PERGUNTA_02_IGNORA_OPTIONS: RadioOption<ConsultaSeguimentoPergunta02IgnoraEnum>[] = [
  {
    label: 'Com frequência ignora sons',
    value: ConsultaSeguimentoPergunta02IgnoraEnum.IGNORA_SONS,
  },
  {
    label: 'Com frequência ignora as pessoas',
    value: ConsultaSeguimentoPergunta02IgnoraEnum.IGNORA_PESSOAS,
  },
]

const PERGUNTA_02_RESULTADO_EXAME_OPTIONS: RadioOption<ConsultaSeguimentoPergunta02ResultadoExameEnum>[] = [
  {
    label: 'Audição na faixa normal',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.AUDICAO_NORMAL,
  },
  {
    label: 'Audição abaixo do normal',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.AUDICAO_ABAIXO,
  },
  {
    label: 'Resultados que não tiveram conclusão ou não definitivos',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.NAO_DEFINITIVO,
  },
]

const conditionToCalculate = (values: ConsultaSeguimentoPergunta02Model): boolean => {
  const hasAnySim = hasPergunta02IgnoraAnySim(values?.PERGUNTA_02_IGNORA)
  const isAllNao = isPergunta02IgnoraAllNao(values?.PERGUNTA_02_IGNORA)

  return hasAnySim || isAllNao
}

const calculateResultado = (values: ConsultaSeguimentoPergunta02Model) => {
  const hasAnySim = hasPergunta02IgnoraAnySim(values?.PERGUNTA_02_IGNORA)
  const isAllNao = isPergunta02IgnoraAllNao(values?.PERGUNTA_02_IGNORA)

  if (hasAnySim) {
    return PassouFalhouEnum.FALHOU
  }

  if (isAllNao) {
    return PassouFalhouEnum.PASSOU
  }
}

const createStyles = (theme: Theme) => ({
  andaSemApoioLabel: css`
    color: ${theme.pallete.primary.main};
  `,
  fezExameBox: css`
    border-color: ${theme.pallete.gray.c80};
    background-color: ${theme.pallete.gray.c90};
  `,
})
